import React, { useState, useEffect, useCallback, useRef } from 'react';
import { VariableSizeList as List } from 'react-window';
import { getItems, getTotalItemCount, getCategories } from '../services/storage';
import { DataItem } from '../types';
import { PAGE_SIZE } from '../constants';
import ListItem from './ListItem';
import LoadingSpinner from './Spinner';
import TableControls from './TableControls';

interface ListProps {
    onItemsCountChange: (count: number) => void;
    onEditItem?: (item: DataItem) => void;
    scrollToTimestamp?: number; // Timestamp to scroll to when provided
    onCurrentWeekChange?: (weekLabel: string) => void; // Callback when visible week changes
}

interface ItemData {
    items: Map<number, DataItem>;
    loadMoreItems: (startIndex: number, stopIndex: number) => Promise<void>;
    isItemLoaded: (index: number) => boolean;
    onEditItem: (item: DataItem) => void;
    onDeleteItem: (item: DataItem) => void;
}

const VirtualList: React.FC<ListProps> = ({ onItemsCountChange, onEditItem, scrollToTimestamp, onCurrentWeekChange }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerHeight, setContainerHeight] = useState(600);
    const [filtersExpanded, setFiltersExpanded] = useState(false);
    const [items, setItems] = useState<Map<number, DataItem>>(new Map());
    const [totalCount, setTotalCount] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [loadedRanges, setLoadedRanges] = useState<Set<string>>(new Set());
    const [categories, setCategories] = useState<string[]>([]);
    
    // Table controls state
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [filterBy, setFilterBy] = useState<string>('workout,meal'); // Default to workouts and meals
    const [sortBy, setSortBy] = useState<keyof DataItem>('timestamp');
    const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
    
    const listRef = useRef<List>(null);
    const loadingPromises = useRef<Map<string, Promise<void>>>(new Map());
    const [currentVisibleWeek, setCurrentVisibleWeek] = useState<string | null>(null);
    
    // Height measurement
    const [itemHeights, setItemHeights] = useState<Map<number, number>>(new Map());
    const BASE_ITEM_HEIGHT = 240; // Estimated base height for loading states
    const WEEK_HEADER_HEIGHT = 28; // pt-1 + mb-1 + text + pb-1 + border

    // Reset cached heights when items change significantly
    useEffect(() => {
        setItemHeights(new Map());
        listRef.current?.resetAfterIndex(0);
    }, [searchTerm, filterBy, sortBy, sortOrder]);

    useEffect(() => {
        onItemsCountChange(items.size);
    }, [items, onItemsCountChange]);

    // Track visible week changes during scroll
    const handleItemsRendered = useCallback((props: { visibleStartIndex: number; visibleStopIndex: number }) => {
        const { visibleStartIndex } = props;
        const visibleItem = items.get(visibleStartIndex);
        
        if (visibleItem) {
            const weekLabel = getWeekLabel(visibleItem.timestamp);
            if (weekLabel !== currentVisibleWeek) {
                setCurrentVisibleWeek(weekLabel);
                if (onCurrentWeekChange) {
                    onCurrentWeekChange(weekLabel);
                }
            }
        }
    }, [items, currentVisibleWeek, onCurrentWeekChange]);

    // Height calculation function
    const updateHeight = useCallback(() => {
        if (containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            const tableControlsElement = containerRef.current.querySelector('.bg-slate-700'); // TableControls container
            const tableControlsHeight = tableControlsElement ? tableControlsElement.getBoundingClientRect().height : 0;
            const availableHeight = rect.height - tableControlsHeight;
            setContainerHeight(Math.max(availableHeight, 300));
        }
    }, []);

    // Dynamic height calculation
    useEffect(() => {
        updateHeight();

        const resizeObserver = new ResizeObserver(updateHeight);
        if (containerRef.current) {
            resizeObserver.observe(containerRef.current);
        }

        return () => {
            resizeObserver.disconnect();
        };
    }, [updateHeight]);

    // Update height when filters expand/collapse
    useEffect(() => {
        const timer = setTimeout(updateHeight, 50); // Small delay to allow DOM to update
        return () => clearTimeout(timer);
    }, [filtersExpanded, updateHeight]);

    // Update height when other items change (which might change TableControls height)
    useEffect(() => {
        const timer = setTimeout(updateHeight, 100);
        return () => clearTimeout(timer);
    }, [searchTerm, filterBy, sortBy, sortOrder, updateHeight]);

    // Initial load - get total count and categories
    useEffect(() => {
        const loadInitialData = async () => {
            setIsLoading(true);
            try {
                const [count, categoriesList] = await Promise.all([
                    getTotalItemCount(getQueryOptions()),
                    getCategories()
                ]);
                setTotalCount(count);
                setCategories(categoriesList);
                
                // Load first page
                if (count > 0) {
                    await loadMoreItems(0, Math.min(PAGE_SIZE - 1, count - 1));
                }
            } catch (err) {
                console.error("Failed to load initial data:", err);
                setError("Failed to load data. Please ensure data is seeded.");
            } finally {
                setIsLoading(false);
            }
        };
        
        loadInitialData();
    }, []);

    // Reload data when filters change
    useEffect(() => {
        const reloadData = async () => {
            setItems(new Map());
            setLoadedRanges(new Set());
            setIsLoading(true);
            
            try {
                const count = await getTotalItemCount(getQueryOptions());
                setTotalCount(count);
                
                // Load first page with new filters
                if (count > 0) {
                    await loadMoreItems(0, Math.min(PAGE_SIZE - 1, count - 1));
                }
                
                // Reset list virtualization and scroll to top
                listRef.current?.resetAfterIndex(0);
                listRef.current?.scrollToItem(0);
            } catch (err) {
                console.error("Failed to reload data:", err);
                setError("Failed to reload data.");
            } finally {
                setIsLoading(false);
            }
        };

        reloadData();
    }, [searchTerm, filterBy, sortBy, sortOrder]);

    const getQueryOptions = useCallback(() => ({
        search: searchTerm,
        filterBy: filterBy,
        sortBy: sortBy,
        sortOrder: sortOrder
    }), [searchTerm, filterBy, sortBy, sortOrder]);

    // Table controls handlers
    const handleSearchChange = useCallback((search: string) => {
        setSearchTerm(search);
    }, []);

    const handleSortChange = useCallback((newSortBy: keyof DataItem, newSortOrder: 'asc' | 'desc') => {
        setSortBy(newSortBy);
        setSortOrder(newSortOrder);
    }, []);

    const handleFilterChange = useCallback((filter: string) => {
        setFilterBy(filter);
    }, []);

    const getRangeKey = (startIndex: number, stopIndex: number): string => {
        const startPage = Math.floor(startIndex / PAGE_SIZE);
        const endPage = Math.floor(stopIndex / PAGE_SIZE);
        return `${startPage}-${endPage}`;
    };

    const loadMoreItems = useCallback(async (startIndex: number, stopIndex: number): Promise<void> => {
        const rangeKey = getRangeKey(startIndex, stopIndex);
        
        // Check if we're already loading this range
        if (loadingPromises.current.has(rangeKey)) {
            return loadingPromises.current.get(rangeKey)!;
        }
        
        // Check if we've already loaded this range
        if (loadedRanges.has(rangeKey)) {
            return Promise.resolve();
        }

        const loadPromise = (async () => {
            try {
                const startPage = Math.floor(startIndex / PAGE_SIZE) + 1;
                const endPage = Math.floor(stopIndex / PAGE_SIZE) + 1;
                
                const loadPromises: Promise<DataItem[]>[] = [];
                for (let page = startPage; page <= endPage; page++) {
                    loadPromises.push(getItems(page, PAGE_SIZE, getQueryOptions()));
                }
                
                const results = await Promise.all(loadPromises);
                
                setItems(prevItems => {
                    const newItems = new Map(prevItems);
                    let currentIndex = (startPage - 1) * PAGE_SIZE;
                    
                    results.forEach(pageItems => {
                        pageItems.forEach(item => {
                            newItems.set(currentIndex++, item);
                        });
                    });
                    
                    return newItems;
                });
                
                setLoadedRanges(prev => new Set([...prev, rangeKey]));
            } catch (err) {
                console.error("Failed to load items:", err);
                setError("Failed to load items.");
            } finally {
                loadingPromises.current.delete(rangeKey);
            }
        })();
        
        loadingPromises.current.set(rangeKey, loadPromise);
        return loadPromise;
    }, [loadedRanges, getQueryOptions]);

    // Function to scroll to a specific week based on timestamp
    const scrollToWeek = useCallback(async (targetTimestamp: number) => {
        if (!listRef.current || totalCount === 0) return;

        const targetWeekLabel = getWeekLabel(targetTimestamp);
        console.log('Scrolling to week:', targetWeekLabel, 'for timestamp:', targetTimestamp);
        
        // First, try to find the item in already loaded items
        let targetIndex = -1;
        for (let i = 0; i < items.size; i++) {
            const item = items.get(i);
            if (item && getWeekLabel(item.timestamp) === targetWeekLabel) {
                targetIndex = i;
                console.log('Found target item at index:', targetIndex, 'item:', item.name);
                break;
            }
        }

        // If not found in loaded items, we need to search all items
        if (targetIndex === -1) {
            console.log('Target week not found in loaded items, searching all items...');
            try {
                // Load all items to find the target week
                const provider = await import('../services/storage').then(m => m);
                const allItems = await provider.getItems(1, totalCount, getQueryOptions());
                
                for (let i = 0; i < allItems.length; i++) {
                    const item = allItems[i];
                    if (getWeekLabel(item.timestamp) === targetWeekLabel) {
                        targetIndex = i;
                        console.log('Found target item in all items at index:', targetIndex, 'item:', item.name);
                        
                        // Ensure this range is loaded
                        await loadMoreItems(i, i);
                        break;
                    }
                }
            } catch (error) {
                console.error('Failed to search for target week:', error);
                return;
            }
        }

        if (targetIndex >= 0) {
            console.log('Scrolling to index:', targetIndex);
            listRef.current.scrollToItem(targetIndex, 'start');
            
            // Update current visible week
            const item = items.get(targetIndex);
            if (item) {
                const newWeek = getWeekLabel(item.timestamp);
                setCurrentVisibleWeek(newWeek);
                if (onCurrentWeekChange) {
                    onCurrentWeekChange(newWeek);
                }
            }
        } else {
            console.log('Target week not found:', targetWeekLabel);
        }
    }, [items, totalCount, getQueryOptions, loadMoreItems, onCurrentWeekChange]);

    // Effect to handle scrolling to specific timestamp
    useEffect(() => {
        if (scrollToTimestamp && totalCount > 0) {
            scrollToWeek(scrollToTimestamp);
        }
    }, [scrollToTimestamp, totalCount, scrollToWeek]);

    const isItemLoaded = useCallback((index: number): boolean => {
        return items.has(index);
    }, [items]);

    const handleEditItem = useCallback((item: DataItem) => {
        console.log('Edit item:', item);
        if (onEditItem) {
            onEditItem(item);
        } else {
            console.log('No edit handler provided');
        }
    }, [onEditItem]);

    const handleDeleteItem = useCallback(async (item: DataItem) => {
        console.log('Delete item:', item);
        
        // Prevent deleting profile items as they are critical
        if (item.category === 'profile') {
            alert('Profile items cannot be deleted as they contain your settings and user data.');
            return;
        }
        
        if (window.confirm(`Are you sure you want to delete "${item.name}"?`)) {
            try {
                setIsLoading(true);
                
                // Import the storage function dynamically
                const { deleteItem } = await import('../services/storage');
                await deleteItem(item.id);
                
                // Remove the item from local state
                setItems(prevItems => {
                    const newItems = new Map(prevItems);
                    // Find and remove the item
                    for (const [index, mapItem] of newItems.entries()) {
                        if (mapItem.id === item.id) {
                            newItems.delete(index);
                            break;
                        }
                    }
                    
                    // Reindex the remaining items to fill gaps
                    const reindexedItems = new Map();
                    let newIndex = 0;
                    for (const [, mapItem] of newItems.entries()) {
                        reindexedItems.set(newIndex++, mapItem);
                    }
                    
                    return reindexedItems;
                });
                
                // Update total count
                setTotalCount(prev => prev - 1);
                
                // Reset the list to refresh properly
                listRef.current?.resetAfterIndex(0);
                
            } catch (error) {
                console.error('Failed to delete item:', error);
                setError('Failed to delete item');
            } finally {
                setIsLoading(false);
            }
        }
    }, []);

    const getWeekLabel = (timestamp: number): string => {
        const date = new Date(timestamp);
        const startOfWeek = new Date(date);
        startOfWeek.setDate(date.getDate() - date.getDay()); // Start of week (Sunday)
        
        const now = new Date();
        const diffTime = now.getTime() - date.getTime();
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays < 7) {
            return "This Week";
        } else if (diffDays < 14) {
            return "Last Week";
        } else {
            const month = startOfWeek.toLocaleDateString('en-US', { month: 'long' });
            const day = startOfWeek.getDate();
            return `Week of ${month} ${day}`;
        }
    };

    const getItemSize = (index: number): number => {
        // Check cached height first
        const cachedHeight = itemHeights.get(index);
        if (cachedHeight) return cachedHeight;

        // Check if this item needs a week header
        const item = items.get(index);
        if (!item) return BASE_ITEM_HEIGHT; // Loading state
        
        const prevItem = index > 0 ? items.get(index - 1) : null;
        const currentWeek = getWeekLabel(item.timestamp);
        const prevWeek = prevItem ? getWeekLabel(prevItem.timestamp) : null;
        const showWeekHeader = !prevItem || currentWeek !== prevWeek;
        
        // Estimate height based on content
        let estimatedHeight = 50; // Base padding and header
        
        // Add week header height if needed
        if (showWeekHeader) {
            estimatedHeight += WEEK_HEADER_HEIGHT;
        }
        
        // Add consistent margin
        estimatedHeight += 4; // mb-1 margin
        
        // Estimate content height based on item type and content
        if (item.category === 'workout' || item.category === 'workout_template') {
            const workoutData = item.data as any;
            estimatedHeight += 80; // Base workout info
            
            // Estimate based on number of exercises (each exercise takes ~20-30px)
            if (workoutData?.exercises?.length) {
                const exerciseRows = Math.ceil(workoutData.exercises.length / 4); // ~4 exercises per row
                estimatedHeight += exerciseRows * 25;
            } else {
                estimatedHeight += 25; // Minimum one row
            }
        } else if (item.category === 'meal' || item.category === 'meal_template') {
            const mealData = item.data as any;
            estimatedHeight += 110; // Base meal info + nutrition labels
            
            // Estimate based on number of portions
            if (mealData?.portions?.length) {
                const portionRows = Math.ceil(mealData.portions.length / 5); // ~5 portions per row
                estimatedHeight += portionRows * 25;
            } else {
                estimatedHeight += 25; // Minimum one row
            }
        } else if (item.category === 'profile') {
            estimatedHeight += 80; // Profile stats and goals
        }
        
        // Add some buffer for text wrapping
        estimatedHeight += 10;
        
        return estimatedHeight;
    };

    const Row = ({ index, style, data }: { index: number; style: React.CSSProperties; data: ItemData }) => {
        const { items, loadMoreItems, isItemLoaded } = data;
        const rowRef = useRef<HTMLDivElement>(null);
        
        useEffect(() => {
            if (!isItemLoaded(index)) {
                loadMoreItems(index, index);
            }
        }, [index, isItemLoaded, loadMoreItems]);

        // Measure actual height after render
        useEffect(() => {
            if (rowRef.current && items.get(index)) {
                const resizeObserver = new ResizeObserver(() => {
                    if (rowRef.current) {
                        const actualHeight = rowRef.current.getBoundingClientRect().height;
                        const currentHeight = itemHeights.get(index);
                        
                        // Only update if height has changed significantly (>5px difference)
                        if (!currentHeight || Math.abs(actualHeight - currentHeight) > 5) {
                            setItemHeights(prev => {
                                const newHeights = new Map(prev);
                                newHeights.set(index, actualHeight);
                                return newHeights;
                            });
                            
                            // Reset the list to recalculate positions
                            listRef.current?.resetAfterIndex(index, false);
                        }
                    }
                });
                
                resizeObserver.observe(rowRef.current);
                
                return () => resizeObserver.disconnect();
            }
        }, [index, items.get(index)]);

        const item = items.get(index);
        const prevItem = index > 0 ? items.get(index - 1) : null;
        
        if (!item) {
            return (
                <div style={style} className="flex justify-center items-center p-4">
                    <LoadingSpinner message="Loading item..." />
                </div>
            );
        }

        // Check if we need to show a week header
        const currentWeek = getWeekLabel(item.timestamp);
        const prevWeek = prevItem ? getWeekLabel(prevItem.timestamp) : null;
        const showWeekHeader = !prevItem || currentWeek !== prevWeek;

        return (
            <div ref={rowRef} style={{ ...style, zIndex: 1 }} className="px-6 relative overflow-visible">
                {showWeekHeader && (
                    <div className="mb-1 pt-1">
                        <h2 className="text-base font-semibold text-slate-300 pb-1 border-b border-slate-700">
                            {currentWeek}
                        </h2>
                    </div>
                )}
                <div className="mb-1">
                    <ListItem 
                        item={item} 
                        onEdit={data.onEditItem}
                        onDelete={data.onDeleteItem}
                    />
                </div>
            </div>
        );
    };

    const itemData: ItemData = {
        items,
        loadMoreItems,
        isItemLoaded,
        onEditItem: handleEditItem,
        onDeleteItem: handleDeleteItem
    };

    // Render content based on state
    const renderContent = () => {
        if (isLoading && items.size === 0) {
            return <LoadingSpinner message="Initializing list..." />;
        }

        if (error && items.size === 0) {
            return <div className="text-center p-8 text-red-400 bg-slate-700 rounded-lg">{error}</div>;
        }

        if (totalCount === 0 && !isLoading) {
            const hasFilters = searchTerm || filterBy;
            return (
                <div className="text-center p-8 text-slate-400 bg-slate-700 rounded-lg">
                    <h3 className="text-2xl font-semibold mb-2">
                        {hasFilters ? "No items match your filters." : "No items found."}
                    </h3>
                    <p>
                        {hasFilters 
                            ? "Try adjusting your search or filter criteria."
                            : "Try seeding some data using the button above!"
                        }
                    </p>
                </div>
            );
        }

        return (
            <div className="w-full pb-20">
                <List
                    ref={listRef}
                    height={containerHeight}
                    itemCount={totalCount}
                    itemSize={getItemSize}
                    itemData={itemData}
                    width="100%"
                    overscanCount={3}
                    onItemsRendered={handleItemsRendered}
                    key={`${searchTerm}-${filterBy}-${sortBy}-${sortOrder}`} // Force re-render when filters change
                >
                    {Row}
                </List>
            </div>
        );
    };

    return (
        <div ref={containerRef} className="w-full h-full">
            <TableControls
                onSearchChange={handleSearchChange}
                onSortChange={handleSortChange}
                onFilterChange={handleFilterChange}
                onExpandChange={setFiltersExpanded}
                categories={categories}
                currentSort={{ sortBy, sortOrder }}
                currentFilter={filterBy}
                currentSearch={searchTerm}
            />
            {renderContent()}
        </div>
    );
};

export default VirtualList;